import { useQuery } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { QueryConfig } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

const url = `${PREFIX_URL}/projects/:projectId/permissions`;

export enum LibraryActionEnum {
  CREATE_FOLDER = 'create_folder',
  MANAGE_ACCESS = 'manage_access',
  CREATE_TAG = 'create_tag',
  VIEW_TAGS = 'view_tags',
  EDIT_TAG = 'edit_tag',
  DELETE_TAG = 'delete_tag',
  ATTACH_TAG = 'attach_tag',
  DETACH_TAG = 'detach_tag',
}

export type LibraryPermissionAction = {
  name: LibraryActionEnum | string;
  allowed: boolean;
};

export enum InboxActionEnum {
  MANAGE_ACCESS = 'manage_access',
  COMPOSE = 'compose',
  CREATE_TAG = 'create_tag',
  VIEW_TAGS = 'view_tags',
  EDIT_TAG = 'edit_tag',
  DELETE_TAG = 'delete_tag',
  ATTACH_TAG = 'attach_tag',
  DETACH_TAG = 'detach_tag',
}

export type ProjectPermissionAction =
  | { name: 'create_folder'; allowed: boolean }
  | { name: 'manage_access'; allowed: boolean }
  | { name: 'create_tag'; allowed: boolean }
  | { name: 'view_tags'; allowed: boolean }
  | { name: 'edit_tag'; allowed: boolean }
  | { name: 'delete_tag'; allowed: boolean }
  | { name: 'attach_tag'; allowed: boolean }
  | { name: 'detach_tag'; allowed: boolean }
  | { name: 'rename'; allowed: boolean }
  | { name: 'compose'; allowed: boolean }
  | { name: 'company_profile'; allowed: boolean }
  | { name: 'apps'; allowed: boolean }
  | { name: 'account_general'; allowed: boolean }
  | { name: 'account_dashboard'; allowed: boolean }
  | { name: 'account_inbox'; allowed: boolean }
  | { name: 'account_members'; allowed: boolean }
  | { name: 'account_workstreams'; allowed: boolean }
  | { name: 'account_calendar'; allowed: boolean };

export type ProjectPermissionModule = {
  access: boolean;
  actions: ProjectPermissionAction[];
};

export type ProjectPermissionsData = {
  library: {
    access: boolean;
    actions: Extract<
      ProjectPermissionAction,
      | { name: 'create_folder' }
      | { name: 'manage_access' }
      | { name: 'create_tag' }
      | { name: 'view_tags' }
      | { name: 'edit_tag' }
      | { name: 'delete_tag' }
      | { name: 'attach_tag' }
      | { name: 'detach_tag' }
      | { name: 'rename' }
    >[];
  };
  inbox: {
    access: boolean;
    actions: Extract<
      ProjectPermissionAction,
      | { name: 'manage_access' }
      | { name: 'compose' }
      | { name: 'create_tag' }
      | { name: 'view_tags' }
      | { name: 'edit_tag' }
      | { name: 'delete_tag' }
      | { name: 'attach_tag' }
      | { name: 'detach_tag' }
    >[];
  };
  settings: {
    access: boolean;
    actions: Extract<
      ProjectPermissionAction,
      | { name: 'company_profile' }
      | { name: 'apps' }
      | { name: 'account_general' }
      | { name: 'account_dashboard' }
      | { name: 'account_inbox' }
      | { name: 'account_members' }
      | { name: 'account_workstreams' }
      | { name: 'account_calendar' }
    >[];
  };
};

export type GetProjectPermissionsResponse = {
  success: boolean;
  data: ProjectPermissionsData;
};

export const getProjectPermissionsQueryKey = {
  list: ['getProjectPermissions'] as const,
};

export const getProjectPermissions = async ({
  projectId,
}: {
  projectId: number;
}): Promise<GetProjectPermissionsResponse> => {
  const URL = generateRoutePath(url, { projectId });
  return axios.get(URL, { showError: false });
};

type QueryFnType = typeof getProjectPermissions;

type UseGetProjectPermissionsOptions = {
  projectId: number;
  config?: QueryConfig<QueryFnType>;
};

export const useGetProjectPermissions = ({
  projectId,
  config = {},
}: UseGetProjectPermissionsOptions) => {
  return useQuery({
    queryKey: [...getProjectPermissionsQueryKey.list, projectId],
    queryFn: async () => {
      const res = await getProjectPermissions({ projectId });
      return res;
    },
    staleTime: Infinity,
    ...config,
  });
};
